/**
 * USUARIOS PAGE - User Management with useServerDataTable hook
 * Clean implementation using reusable DataTable components
 *
 * MIGRACIÓN A TANSTACK QUERY:
 * - useRoles() y useSucursales() para cargar opciones de filtros (cache automático)
 * - useActivateUser(), useDeactivateUser(), useDeleteUser() para mutaciones
 * - La lista de usuarios sigue usando useServerDataTable (ya tiene su propia lógica)
 */

import { useEffect, useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type { ColumnDef } from "@tanstack/react-table";
import {
  UserCog,
  Plus,
  Search,
  MoreHorizontal,
  Pencil,
  KeyRound,
  UserCheck,
  UserX,
  AlertCircle,
  RefreshCw,
  Trash2,
  X,
  Mail,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  DataTableColumnHeader,
  DataTablePagination,
  DataTableViewOptions,
} from "@/components/ui/data-table";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { ResponsiveTable, useIsMobile } from "@/components/ui/responsive-table";

import type { User } from "@/types/user";
import { getUsers } from "@/hooks/use-users";
import { useApiError } from "@/hooks/use-api-error";
import { useServerDataTable } from "@/hooks/use-server-data-table";
import { useSucursal, ALL_SUCURSALES_ID } from "@/contexts/sucursal-context";
import { useSucursales } from "@/hooks/use-sucursales";
import { useRoles, useActivateUser, useDeactivateUser, useDeleteUser } from "@/hooks/use-users";

import { UserFormDialog } from "./components/user-form-dialog";
import { ChangePasswordDialog } from "./components/change-password-dialog";
import { getSucursalDisplay } from "./utils/sucursal-display";

export default function UsuariosPage() {
  const { t } = useTranslation();
  const { getErrorMessage } = useApiError();
  const { sucursalActiva, canViewAllSucursales, isAllSucursalesSelected } = useSucursal();
  const isMobile = useIsMobile();

  // =========================================================================
  // TANSTACK QUERY - Carga de datos para filtros
  // =========================================================================
  // Antes: useEffect + useState para cargar roles y sucursales
  // Ahora: hooks que usan useQuery (cache automático, deduplicación)

  // Roles para el dropdown de filtro
  const { data: roles = [] } = useRoles();

  // Sucursales para el dropdown de filtro
  // Nota: useSucursales ya está siendo usado por SucursalContext,
  // pero gracias al cache de TanStack Query, NO hace otro request
  const { data: sucursales = [] } = useSucursales();

  // =========================================================================
  // TANSTACK QUERY - Mutaciones (activar, desactivar, eliminar)
  // =========================================================================
  // Antes: funciones async con try/catch manual
  // Ahora: hooks que manejan estados y callbacks automáticamente

  const activateUserMutation = useActivateUser({
    onSuccess: () => {
      toast.success(t("usuarios.toast.activated", { name: selectedUser?.nombre }));
      refetch(); // Recargar la tabla
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const deactivateUserMutation = useDeactivateUser({
    onSuccess: () => {
      toast.warning(t("usuarios.toast.deactivated", { name: selectedUser?.nombre }));
      refetch();
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const deleteUserMutation = useDeleteUser({
    onSuccess: () => {
      toast.error(t("usuarios.toast.deleted", { name: selectedUser?.nombre }));
      refetch();
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  // Dialog state
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isPasswordOpen, setIsPasswordOpen] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [confirmAction, setConfirmAction] = useState<"activate" | "deactivate" | "delete" | null>(
    null
  );

  // Determine initial sucursal filter based on context
  const getInitialSucursalFilter = useCallback(() => {
    if (canViewAllSucursales && isAllSucursalesSelected) {
      return "all";
    }
    if (sucursalActiva && sucursalActiva.id !== ALL_SUCURSALES_ID) {
      return String(sucursalActiva.id);
    }
    return "all";
  }, [canViewAllSucursales, isAllSucursalesSelected, sucursalActiva]);

  // Action handlers (memoized for column definitions)
  const handleEditUser = useCallback((user: User) => {
    setSelectedUser(user);
    setIsFormOpen(true);
  }, []);

  const handleChangePassword = useCallback((user: User) => {
    setSelectedUser(user);
    setIsPasswordOpen(true);
  }, []);

  const handleToggleStatus = useCallback((user: User) => {
    setSelectedUser(user);
    setConfirmAction(user.is_active ? "deactivate" : "activate");
    setIsConfirmOpen(true);
  }, []);

  const handleDeleteUser = useCallback((user: User) => {
    setSelectedUser(user);
    setConfirmAction("delete");
    setIsConfirmOpen(true);
  }, []);

  // Column definitions
  const columns: ColumnDef<User>[] = useMemo(
    () => [
      {
        accessorKey: "nombre",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t("usuarios.columns.name")} />
        ),
        cell: ({ row }) => (
          <span className="font-medium">
            {row.original.nombre} {row.original.apellido}
          </span>
        ),
      },
      {
        accessorKey: "email",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t("usuarios.columns.email")} />
        ),
      },
      {
        accessorKey: "role__name",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t("usuarios.columns.role")} />
        ),
        cell: ({ row }) =>
          row.original.role ? (
            <Badge variant="outline">{row.original.role.name}</Badge>
          ) : (
            <span className="text-muted-foreground">-</span>
          ),
      },
      {
        accessorKey: "sucursal__nombre",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t("usuarios.columns.sucursal")} />
        ),
        enableSorting: false,
        cell: ({ row }) => {
          const user = row.original;
          const isAdminGlobal = user.role?.name === "Administrador Global";
          const display = getSucursalDisplay(user.sucursales || [], isAdminGlobal);

          if (display.showAll) {
            return <Badge variant="secondary">{t("usuarios.allSucursales")}</Badge>;
          }
          if (display.visible.length === 0) {
            return <span className="text-muted-foreground">-</span>;
          }
          return (
            <div className="flex gap-1 flex-wrap">
              {display.visible.map((s) => (
                <Badge key={s.id} variant="outline">
                  {s.nombre}
                </Badge>
              ))}
              {display.remaining > 0 && <Badge variant="secondary">+{display.remaining}</Badge>}
            </div>
          );
        },
      },
      {
        accessorKey: "is_active",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t("usuarios.columns.status")} />
        ),
        cell: ({ row }) => (
          <Badge variant={row.original.is_active ? "default" : "secondary"}>
            {row.original.is_active ? t("usuarios.active") : t("usuarios.inactive")}
          </Badge>
        ),
      },
      {
        id: "actions",
        header: () => t("common.actions"),
        cell: ({ row }) => {
          const user = row.original;
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon">
                  <MoreHorizontal className="h-4 w-4" />
                  <span className="sr-only">{t("common.actions")}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleEditUser(user)}>
                  <Pencil className="mr-2 h-4 w-4" />
                  {t("common.edit")}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleChangePassword(user)}>
                  <KeyRound className="mr-2 h-4 w-4" />
                  {t("usuarios.changePassword")}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => handleToggleStatus(user)}>
                  {user.is_active ? (
                    <>
                      <UserX className="mr-2 h-4 w-4" />
                      {t("usuarios.deactivate")}
                    </>
                  ) : (
                    <>
                      <UserCheck className="mr-2 h-4 w-4" />
                      {t("usuarios.activate")}
                    </>
                  )}
                </DropdownMenuItem>
                {!user.is_active && (
                  <DropdownMenuItem
                    onClick={() => handleDeleteUser(user)}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    {t("common.delete")}
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
      },
    ],
    [t, handleEditUser, handleChangePassword, handleToggleStatus, handleDeleteUser]
  );

  // Server-side data table hook (ahora con TanStack Query)
  const {
    table,
    isLoading,
    error,
    totalCount,
    refetch,
    searchTerm,
    setSearchTerm,
    filters,
    setFilter,
    clearFilters,
    hasActiveFilters,
  } = useServerDataTable({
    columns,
    fetchFn: getUsers,
    queryKey: "users", // Cache key para TanStack Query
    storageKey: "usuarios_page_size",
    filters: [
      { key: "role", defaultValue: "all" },
      { key: "sucursal", defaultValue: "all" },
      { key: "is_active", defaultValue: "all", urlParam: "status" },
    ],
    initialFilters: {
      sucursal: getInitialSucursalFilter(),
    },
  });

  // Sync sucursal filter when context changes (only when header selection changes)
  // This should NOT run when the user changes the table filter manually
  useEffect(() => {
    // Solo sincronizar cuando cambia la sucursal del header, no cuando cambia el filtro de la tabla
    if (!isAllSucursalesSelected && sucursalActiva && sucursalActiva.id !== ALL_SUCURSALES_ID) {
      // Sucursal específica seleccionada en el header → forzar ese filtro
      const sucursalId = String(sucursalActiva.id);
      if (filters.sucursal !== sucursalId) {
        setFilter("sucursal", sucursalId);
      }
    }
    // Cuando isAllSucursalesSelected es true, NO sincronizamos - dejamos que el usuario elija libremente
  }, [sucursalActiva, isAllSucursalesSelected, filters.sucursal, setFilter]);

  // Handlers
  const handleCreateUser = () => {
    setSelectedUser(null);
    setIsFormOpen(true);
  };

  const handleConfirmAction = async () => {
    if (!selectedUser || !confirmAction) return;

    // Usar las mutaciones de TanStack Query
    // mutate() es async pero no retorna Promise - los callbacks manejan el resultado
    if (confirmAction === "activate") {
      activateUserMutation.mutate(selectedUser.id);
    } else if (confirmAction === "deactivate") {
      deactivateUserMutation.mutate(selectedUser.id);
    } else if (confirmAction === "delete") {
      deleteUserMutation.mutate(selectedUser.id);
    }

    // Cerrar el diálogo inmediatamente
    // Los toasts se muestran en los callbacks onSuccess/onError
    setIsConfirmOpen(false);
    setSelectedUser(null);
    setConfirmAction(null);
  };

  const handleFormSuccess = (isNew: boolean) => {
    setIsFormOpen(false);
    toast.success(isNew ? t("usuarios.toast.created") : t("usuarios.toast.updated"));
    setSelectedUser(null);
    refetch();
  };

  const handlePasswordSuccess = () => {
    setIsPasswordOpen(false);
    toast.success(t("usuarios.toast.passwordChanged"));
    setSelectedUser(null);
  };

  // Column labels for view options
  const columnLabels = {
    nombre: t("usuarios.columns.name"),
    email: t("usuarios.columns.email"),
    role__name: t("usuarios.columns.role"),
    sucursal__nombre: t("usuarios.columns.sucursal"),
    is_active: t("usuarios.columns.status"),
  };

  // Mobile card render function
  const renderMobileCard = useCallback(
    (user: User) => (
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium">
              {user.nombre} {user.apellido}
            </span>
            <Badge variant={user.is_active ? "default" : "secondary"} className="shrink-0">
              {user.is_active ? t("usuarios.active") : t("usuarios.inactive")}
            </Badge>
          </div>
          <div className="flex items-center gap-1 mt-1 text-sm text-muted-foreground">
            <Mail className="h-3 w-3" />
            <span className="truncate">{user.email}</span>
          </div>
          {user.role && (
            <Badge variant="outline" className="mt-1 text-xs">
              {user.role.name}
            </Badge>
          )}
          {user.role?.name === "Administrador Global" ? (
            <Badge variant="secondary" className="mt-1 text-xs">
              {t("usuarios.allSucursales")}
            </Badge>
          ) : user.sucursales && user.sucursales.length > 0 ? (
            <div className="flex gap-1 flex-wrap mt-1">
              {user.sucursales.slice(0, 2).map((s) => (
                <span key={s.id} className="text-sm text-muted-foreground">
                  {s.nombre}
                </span>
              ))}
              {user.sucursales.length > 2 && (
                <span className="text-sm text-muted-foreground">+{user.sucursales.length - 2}</span>
              )}
            </div>
          ) : null}
          <div className="flex gap-1 mt-3">
            <Button variant="outline" size="sm" onClick={() => handleEditUser(user)}>
              <Pencil className="h-3 w-3 mr-1" />
              {t("common.edit")}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <MoreHorizontal className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleChangePassword(user)}>
                  <KeyRound className="mr-2 h-4 w-4" />
                  {t("usuarios.changePassword")}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => handleToggleStatus(user)}>
                  {user.is_active ? (
                    <>
                      <UserX className="mr-2 h-4 w-4" />
                      {t("usuarios.deactivate")}
                    </>
                  ) : (
                    <>
                      <UserCheck className="mr-2 h-4 w-4" />
                      {t("usuarios.activate")}
                    </>
                  )}
                </DropdownMenuItem>
                {!user.is_active && (
                  <DropdownMenuItem
                    onClick={() => handleDeleteUser(user)}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    {t("common.delete")}
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    ),
    [t, handleEditUser, handleChangePassword, handleToggleStatus, handleDeleteUser]
  );

  return (
    <div className="flex flex-col gap-6 p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <UserCog className="h-8 w-8 text-primary" />
          <div>
            <h1 className="text-xl sm:text-2xl font-bold">{t("usuarios.title")}</h1>
            <p className="text-sm text-muted-foreground hidden sm:block">
              {t("usuarios.description")}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size={isMobile ? "sm" : "default"}
            onClick={() => refetch()}
            disabled={isLoading}
          >
            <RefreshCw
              className={`h-4 w-4 ${isLoading ? "animate-spin" : ""} ${isMobile ? "" : "mr-2"}`}
            />
            {!isMobile && t("common.refresh")}
          </Button>
          <Button size={isMobile ? "sm" : "default"} onClick={handleCreateUser}>
            <Plus className={`h-4 w-4 ${isMobile ? "" : "mr-2"}`} />
            {isMobile ? t("common.new") : t("usuarios.newUser")}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">{t("common.filter")}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:gap-4">
            <div className="relative flex-1 min-w-0 sm:min-w-[200px]">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder={t("usuarios.searchPlaceholder")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>

            <div className="flex flex-wrap gap-2">
              <Select value={filters.role} onValueChange={(v) => setFilter("role", v)}>
                <SelectTrigger className="w-full sm:w-[180px]">
                  <SelectValue placeholder={t("usuarios.filterByRole")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("usuarios.allRoles")}</SelectItem>
                  {roles.map((role) => (
                    <SelectItem key={role.id} value={String(role.id)}>
                      {role.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Filtro de sucursal: 
                  - Si hay sucursal específica seleccionada en el header → mostrar solo esa (sin dropdown)
                  - Si es Admin Global con "Todas" seleccionada → mostrar dropdown con todas las opciones
              */}
              {isAllSucursalesSelected ? (
                // Admin Global con "Todas" seleccionada - puede filtrar por cualquier sucursal
                <Select value={filters.sucursal} onValueChange={(v) => setFilter("sucursal", v)}>
                  <SelectTrigger className="w-full sm:w-[180px]">
                    <SelectValue placeholder={t("usuarios.filterBySucursal")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("usuarios.allSucursales")}</SelectItem>
                    {sucursales.map((sucursal) => (
                      <SelectItem key={sucursal.id} value={String(sucursal.id)}>
                        {sucursal.nombre}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                // Sucursal específica seleccionada - mostrar solo esa (sin opción de cambiar)
                <div className="flex items-center h-10 px-3 border rounded-md bg-muted/50 text-sm">
                  <span className="text-muted-foreground mr-2">
                    {t("usuarios.columns.sucursal")}:
                  </span>
                  <span className="font-medium">{sucursalActiva?.nombre || "-"}</span>
                </div>
              )}

              <Select value={filters.is_active} onValueChange={(v) => setFilter("is_active", v)}>
                <SelectTrigger className="w-full sm:w-[150px]">
                  <SelectValue placeholder={t("usuarios.filterByStatus")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("usuarios.allStatus")}</SelectItem>
                  <SelectItem value="true">{t("usuarios.active")}</SelectItem>
                  <SelectItem value="false">{t("usuarios.inactive")}</SelectItem>
                </SelectContent>
              </Select>

              {/* Mostrar botón limpiar filtros solo si hay filtros activos que el usuario puede limpiar.
                  Cuando hay sucursal bloqueada, no contar el filtro de sucursal como "activo" */}
              {(() => {
                const hasEditableFilters = isAllSucursalesSelected
                  ? hasActiveFilters
                  : searchTerm !== "" || filters.role !== "all" || filters.is_active !== "all";
                return hasEditableFilters ? (
                  <Button
                    variant="secondary"
                    size="icon"
                    onClick={clearFilters}
                    className="shrink-0"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                ) : null;
              })()}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 rounded-md bg-destructive/10 p-4 text-destructive">
          <AlertCircle className="h-5 w-5" />
          <span>{error}</span>
        </div>
      )}

      {/* Table */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardDescription>{t("usuarios.showingResults", { count: totalCount })}</CardDescription>
            {!isMobile && <DataTableViewOptions table={table} columnLabels={columnLabels} />}
          </div>
        </CardHeader>
        <CardContent>
          <ResponsiveTable
            table={table}
            columns={columns}
            isLoading={isLoading}
            emptyMessage={t("usuarios.noUsers")}
            hideOnTablet={["sucursal__nombre", "role__name", "email"]}
            renderMobileCard={renderMobileCard}
          />
          <DataTablePagination table={table} totalRows={totalCount} />
        </CardContent>
      </Card>

      {/* Dialogs */}
      <UserFormDialog
        open={isFormOpen}
        onOpenChange={setIsFormOpen}
        user={selectedUser}
        roles={roles}
        sucursales={sucursales}
        onSuccess={handleFormSuccess}
      />

      <ChangePasswordDialog
        open={isPasswordOpen}
        onOpenChange={setIsPasswordOpen}
        user={selectedUser}
        onSuccess={handlePasswordSuccess}
      />

      <ConfirmDialog
        open={isConfirmOpen}
        onOpenChange={setIsConfirmOpen}
        title={
          confirmAction === "activate"
            ? t("usuarios.confirmActivate")
            : confirmAction === "delete"
              ? t("usuarios.confirmDelete")
              : t("usuarios.confirmDeactivate")
        }
        description={
          confirmAction === "activate"
            ? t("usuarios.confirmActivateDesc", { name: selectedUser?.nombre })
            : confirmAction === "delete"
              ? t("usuarios.confirmDeleteDesc", { name: selectedUser?.nombre })
              : t("usuarios.confirmDeactivateDesc", { name: selectedUser?.nombre })
        }
        onConfirm={handleConfirmAction}
        variant={
          confirmAction === "deactivate" || confirmAction === "delete" ? "destructive" : "default"
        }
      />
    </div>
  );
}
